import { TTypeOfCodec } from "../../shared/src/codecs/codec";
import { boolean } from "../../shared/src/codecs/types/boolean";
import { date } from "../../shared/src/codecs/types/date";
import { dateTime } from "../../shared/src/codecs/types/dateTime";
import { email } from "../../shared/src/codecs/types/email";
import { integer } from "../../shared/src/codecs/types/integer";
import { intersection } from "../../shared/src/codecs/types/intersection";
import { literal } from "../../shared/src/codecs/types/literal";
import { nullCodec } from "../../shared/src/codecs/types/nullCodec";
import { phoneNumber } from "../../shared/src/codecs/types/phoneNumber";
import { positiveDecimal } from "../../shared/src/codecs/types/positiveDecimal";
import { required } from "../../shared/src/codecs/types/required";
import { string } from "../../shared/src/codecs/types/string";
import { union } from "../../shared/src/codecs/types/union";
import { uuid } from "../../shared/src/codecs/types/uuid";
import { PifRiskRuleCode, PifRiskSeverity } from "./PifRiskRuleCode";

const nb = () => union([nullCodec(), boolean()]);
const ni = () => union([nullCodec(), integer()]);
const ns = () => union([nullCodec(), string()]);
const nd = () => union([nullCodec(), date()]);
const npd = () => union([nullCodec(), positiveDecimal()]);
const nemail = () => union([nullCodec(), email()]);
const nphone = () => union([nullCodec(), phoneNumber()]);

export const CasePifPropertyType = union([
    nullCodec(),
    literal("detached"), literal("semi_detached"), literal("terraced"),
    literal("flat"), literal("maisonette"), literal("bungalow"),
    literal("cottage"), literal("other"),
]);
export type TCasePifPropertyType = TTypeOfCodec<typeof CasePifPropertyType>;

export const CasePifExtensionApproved = union([
    nullCodec(),
    literal("yes_both"), literal("planning_only"), literal("building_regs_only"),
    literal("neither"), literal("not_required"), literal("unknown"),
]);
export type TCasePifExtensionApproved = TTypeOfCodec<typeof CasePifExtensionApproved>;

export const CasePifYesNoUnknown = union([
    nullCodec(),
    literal("yes"), literal("no"), literal("unknown"),
]);
export type TCasePifYesNoUnknown = TTypeOfCodec<typeof CasePifYesNoUnknown>;

export const CasePifInclusion = union([
    nullCodec(),
    literal("included"), literal("excluded"), literal("none"),
]);
export type TCasePifInclusion = TTypeOfCodec<typeof CasePifInclusion>;

export const CasePifInclusionNoNone = union([
    nullCodec(),
    literal("included"), literal("excluded"),
]);
export type TCasePifInclusionNoNone = TTypeOfCodec<typeof CasePifInclusionNoNone>;

export const CasePifSewerageConnection = union([
    nullCodec(),
    literal("mains"), literal("septic_tank"), literal("cesspit"),
    literal("treatment_plant"), literal("other"),
]);
export type TCasePifSewerageConnection = TTypeOfCodec<typeof CasePifSewerageConnection>;

export const CasePifHeatingType = union([
    nullCodec(),
    literal("gas_central"), literal("oil"), literal("electric"),
    literal("heat_pump_air"), literal("heat_pump_ground"), literal("heat_pump_water"),
    literal("solid_fuel"), literal("none"), literal("other"),
]);
export type TCasePifHeatingType = TTypeOfCodec<typeof CasePifHeatingType>;

export const CasePifBroadbandType = union([
    nullCodec(),
    literal("fibre_full"), literal("fibre_cabinet"), literal("adsl"),
    literal("cable"), literal("none"), literal("unknown"),
]);
export type TCasePifBroadbandType = TTypeOfCodec<typeof CasePifBroadbandType>;

export const CasePifMobileSignal = union([
    nullCodec(),
    literal("good"), literal("moderate"), literal("poor"),
    literal("none"), literal("unknown"),
]);
export type TCasePifMobileSignal = TTypeOfCodec<typeof CasePifMobileSignal>;

export const CasePifInUlez = union([
    nullCodec(),
    literal("yes"), literal("no"), literal("not_applicable"),
]);
export type TCasePifInUlez = TTypeOfCodec<typeof CasePifInUlez>;

export const CasePifBoundaryOwner = union([
    nullCodec(),
    literal("seller"), literal("neighbour"), literal("shared"), literal("unknown"),
]);
export type TCasePifBoundaryOwner = TTypeOfCodec<typeof CasePifBoundaryOwner>;

export const CasePifOwnershipType = union([
    nullCodec(),
    literal("sole"), literal("joint_tenants"), literal("tenants_in_common"),
]);
export type TCasePifOwnershipType = TTypeOfCodec<typeof CasePifOwnershipType>;

export const CasePifEpcRating = union([
    nullCodec(),
    literal("A"), literal("B"), literal("C"), literal("D"),
    literal("E"), literal("F"), literal("G"), literal("exempt"),
]);
export type TCasePifEpcRating = TTypeOfCodec<typeof CasePifEpcRating>;

export const CasePifDependentPurchaseStatus = union([
    nullCodec(),
    literal("searching"), literal("offer_made"), literal("offer_accepted"), literal("conveyancing"),
]);
export type TCasePifDependentPurchaseStatus = TTypeOfCodec<typeof CasePifDependentPurchaseStatus>;

export const CasePifTenureType = union([
    nullCodec(),
    literal("freehold"), literal("managed_freehold"), literal("leasehold"),
    literal("commonhold"), literal("shared_ownership"), literal("other"),
]);
export type TCasePifTenureType = TTypeOfCodec<typeof CasePifTenureType>;

export const CasePifRentFrequency = union([
    nullCodec(),
    literal("weekly"), literal("monthly"), literal("quarterly"), literal("annually"),
]);
export type TCasePifRentFrequency = TTypeOfCodec<typeof CasePifRentFrequency>;

export const CasePifYesNoNa = union([
    nullCodec(),
    literal("yes"), literal("no"), literal("not_applicable"),
]);
export type TCasePifYesNoNa = TTypeOfCodec<typeof CasePifYesNoNa>;

export const CasePifConsentStatus = union([
    nullCodec(),
    literal("yes"), literal("no"), literal("not_required"),
]);
export type TCasePifConsentStatus = TTypeOfCodec<typeof CasePifConsentStatus>;

export const CasePifCouncilTaxBand = union([
    nullCodec(),
    literal("A"), literal("B"), literal("C"), literal("D"),
    literal("E"), literal("F"), literal("G"), literal("H"),
    literal("I_wales_only"), literal("exempt"),
]);
export type TCasePifCouncilTaxBand = TTypeOfCodec<typeof CasePifCouncilTaxBand>;

export const CasePif1 = required({
    property_type: CasePifPropertyType,
    property_type_other: ns(),
    num_bedrooms: ni(),
    num_bathrooms: ni(),
    num_stories: ni(),
    year_built: ns(),
    has_commercial: nb(),
    step_free_street: nb(),
    step_free_throughout: nb(),

    has_neighbour_disputes: nb(),
    has_legal_action: nb(),
    has_antisocial_behaviour: nb(),
    has_noise_issues: nb(),

    has_extensions: nb(),
    extension_approved: CasePifExtensionApproved,
    has_loft_conversion: nb(),
    has_walls_removed: nb(),
    has_other_structural: nb(),
    has_significant_repairs: nb(),
    has_change_of_use: nb(),

    has_planning_notices: nb(),
    has_building_control_notices: nb(),
    has_environmental_notices: nb(),
    has_other_notices: nb(),

    has_japanese_knotweed: nb(),
    has_flooding: nb(),
    has_subsidence: nb(),
    has_asbestos: nb(),
    has_radon: CasePifYesNoUnknown,
    has_coastal_erosion: nb(),
    has_mining: CasePifYesNoUnknown,
    has_listed_building: nb(),

    kitchen_fitted_units: CasePifInclusion,
    kitchen_appliances: CasePifInclusion,
    light_fittings: CasePifInclusionNoNone,
    curtains_blinds: CasePifInclusion,
    carpets: CasePifInclusion,
    bathroom_fittings: CasePifInclusionNoNone,
    garden_shed_greenhouse: CasePifInclusion,
    satellite_dish_aerial: CasePifInclusion,

    electricity_supplier: ns(),
    gas_connected: nb(),
    gas_supplier: ns(),
    water_supplier: ns(),
    water_meter: nb(),
    sewerage_connection: CasePifSewerageConnection,
    sewerage_other: ns(),
    heating_type: CasePifHeatingType,
    heating_other: ns(),
    boiler_age: ni(),
    broadband_type: CasePifBroadbandType,
    mobile_signal: CasePifMobileSignal,
    solar_panels: nb(),
    in_ulez: CasePifInUlez,

    buildings_insurance_provider: ns(),
    insurance_policy_number: ns(),
    has_insurance_claims: nb(),
    has_insurance_refused: nb(),
    has_title_indemnity: nb(),

    boundary_front: CasePifBoundaryOwner,
    boundary_rear: CasePifBoundaryOwner,
    boundary_left: CasePifBoundaryOwner,
    boundary_right: CasePifBoundaryOwner,
    has_boundary_disputes: nb(),
    boundary_agreements: nb(),

    has_rights_of_way: nb(),
    has_shared_access: nb(),
    has_informal_arrangements: nb(),
    has_easements: nb(),

    has_contamination: nb(),
    has_trees: nb(),
    has_pest_issues: nb(),
    has_damp_issues: nb(),

    ownership_type: CasePifOwnershipType,
    all_owners_agree: nb(),
    has_restrictions: nb(),
    has_charges: nb(),

    boundaries_match_title: nb(),
    has_boundary_changes: nb(),

    has_services_crossing: nb(),
    has_neighbour_services: nb(),
    has_shared_drains: nb(),

    epc_rating: CasePifEpcRating,
    epc_certificate_number: ns(),
    epc_expiry_date: nd(),
    has_green_deal: nb(),

    has_new_build_warranty: nb(),
    has_damp_guarantee: nb(),
    has_timber_guarantee: nb(),
    has_window_guarantee: nb(),
    has_electrical_guarantee: nb(),
    has_roofing_guarantee: nb(),
    has_indemnity_policies: nb(),

    is_occupied: nb(),
    has_tenants: nb(),
    all_occupiers_will_vacate: nb(),

    preferred_completion_date: nd(),
    is_chain: nb(),
    has_dependent_purchase: nb(),
    dependent_purchase_status: CasePifDependentPurchaseStatus,

    has_freehold_share: nb(),
    has_transfer_fee: nb(),
    has_other_legal_info: nb(),

    lease_term_years: ni(),
    lease_ground_rent_pcy_full_currency: npd(),
    lease_service_charge_pcy_full_currency: npd(),
    has_management_company: nb(),
    management_company_name: ns(),
    management_company_contact_name: ns(),
    management_company_email: nemail(),
    management_company_phone_number: nphone(),

    uprn: ns(),
    has_sale_delay_factors: nb(),
    tenure_type: CasePifTenureType,
    shared_ownership_share_percentage: ni(),
    shared_ownership_rent_full_currency: npd(),
    shared_ownership_rent_frequency: CasePifRentFrequency,
    lease_start_date: nd(),
    has_ground_rent_increase: nb(),
    has_reserve_fund: nb(),
    reserve_fund_included_in_service_charge: nb(),
    has_large_expense_notice: nb(),
    additional_fees_on_sale_or_letting: ns(),
    freeholder_name: ns(),
    freeholder_address: ns(),
    freeholder_email: nemail(),
    managing_agent_name: ns(),
    managing_agent_address: ns(),
    managing_agent_email: nemail(),
    owner_required_to_be_director: nb(),

    parking_garage: nb(),
    parking_allocated: nb(),
    parking_driveway: nb(),
    parking_on_street: nb(),
    parking_resident_permit: nb(),
    parking_permit_cost_full_currency: npd(),
    parking_metered: nb(),
    parking_unallocated_communal: nb(),
    parking_controlled_zone: nb(),
    parking_none: nb(),
    parking_other: nb(),
    parking_vehicle_restriction: ns(),
    has_ev_charge_point: nb(),
    has_ev_charge_points_nearby: nb(),

    has_conservation_area: nb(),
    has_tree_preservation_order: nb(),
    tree_work_consents_obtained: CasePifYesNoNa,

    is_standard_construction: nb(),
    has_flat_roof: nb(),
    flat_roof_percentage: ni(),
    flat_roof_last_renewed_year: ns(),
    has_building_safety_remediation_required: nb(),
    has_energy_retrofit: nb(),
    has_spray_foam: nb(),
    is_above_two_storeys: nb(),
    building_storey_count: ni(),
    property_storey_location: ns(),
    is_mixed_use_building: nb(),

    council_tax_band: CasePifCouncilTaxBand,
    has_altered_for_council_tax_band: nb(),

    has_window_replacement: nb(),
    has_conservatory_added: nb(),
    alteration_building_regs_approval: CasePifConsentStatus,
    alteration_planning_permission: CasePifConsentStatus,
    alteration_listed_or_conservation_consent: CasePifConsentStatus,
    alteration_deed_restriction_consent: CasePifConsentStatus,
    alteration_works_unfinished: nb(),
    has_planning_breach: nb(),
    has_unresolved_building_control: nb(),
    will_pay_for_indemnity_for_breach: nb(),

    has_notice_neighbour_development: nb(),
    has_notice_planning_application: nb(),
    has_notice_required_maintenance: nb(),
    has_notice_listed_building_application: nb(),
    has_notice_infrastructure_project: nb(),
    has_notice_party_wall: nb(),

    has_authority_notified_safety: nb(),

    additional_furniture_to_include: ns(),

    lpg_connected: nb(),
    lpg_supplier: ns(),
    oil_connected: nb(),
    oil_supplier: ns(),
    has_cable_tv_satellite: nb(),
    has_sustainable_drainage_system: nb(),
    has_septic_tank: nb(),
    septic_tank_last_emptied_date: nd(),
    has_cesspit: nb(),
    cesspit_last_emptied_date: nd(),
    has_sewage_treatment_plant: nb(),
    sewage_treatment_plant_install_date: nd(),
    sewage_treatment_plant_last_serviced_date: nd(),
    sewage_treatment_plant_building_regs_approved: nb(),
    off_mains_other_properties_connect: nb(),
    off_mains_off_site: nb(),
    off_mains_environment_agency_registered: CasePifYesNoNa,
    off_mains_drains_into_waterway: nb(),
    off_mains_complies_with_general_binding_rules: nb(),
    has_utility_licences_or_maintenance_agreements: nb(),
    heating_install_date: nd(),
    heating_last_serviced_date: nd(),
    heating_in_good_working_order: nb(),

    is_property_insured: nb(),

    has_part_outside_legal_ownership: nb(),
    has_part_on_separate_title: nb(),

    has_access_blocked_or_charged: nb(),
    has_public_right_of_way: nb(),
    has_rights_of_light: nb(),
    has_rights_through_custom: nb(),
    has_mines_and_minerals: nb(),
    has_chancel_liability: nb(),

    has_crime_or_death: nb(),
    has_failed_transactions: nb(),

    has_other_material_issue: nb(),

    has_disputes_or_complaints: nb(),
    has_received_notices: nb(),
    has_specialist_issues: nb(),
    has_environmental_issues: nb(),
    has_guarantees_or_warranties: nb(),
    has_boundary_issues: nb(),
    has_alteration_consents_required: nb(),
    has_other_rights_or_arrangements: nb(),
});
export type TCasePif1Codec = typeof CasePif1;
export type TCasePif1 = TTypeOfCodec<TCasePif1Codec>;

export const CasePifMeta = required({
    id: uuid(),
    case_id: uuid(),
    last_edited_at: dateTime(),
});
export type TCasePifMetaCodec = typeof CasePifMeta;
export type TCasePifMeta = TTypeOfCodec<TCasePifMetaCodec>;

export const CasePifWithMeta = intersection([CasePif1, CasePifMeta]);
export type TCasePifWithMetaCodec = typeof CasePifWithMeta;
export type TCasePifWithMeta = TTypeOfCodec<TCasePifWithMetaCodec>;

export const CasePifFreeText = required({
    case_id: uuid(),
    field_key: string(),
    value: string(),
});
export type TCasePifFreeTextCodec = typeof CasePifFreeText;
export type TCasePifFreeText = TTypeOfCodec<TCasePifFreeTextCodec>;

export const CasePifSignature = required({
    id: uuid(),
    case_id: uuid(),
    user_id: uuid(),
    signed_at: dateTime(),
    signed_name: string(),
    ip: ns(),
    user_agent: ns(),
    screen: ns(),
    timezone: ns(),
    language: ns(),
});
export type TCasePifSignatureCodec = typeof CasePifSignature;
export type TCasePifSignature = TTypeOfCodec<TCasePifSignatureCodec>;

export const CasePifRiskFlag = required({
    case_id: uuid(),
    rule_code: PifRiskRuleCode,
    severity: PifRiskSeverity,
    computed_at: dateTime(),
});
export type TCasePifRiskFlagCodec = typeof CasePifRiskFlag;
export type TCasePifRiskFlag = TTypeOfCodec<TCasePifRiskFlagCodec>;

export const CasePifAutoAnswered = required({
    case_id: uuid(),
    field_key: string(),
});
export type TCasePifAutoAnsweredCodec = typeof CasePifAutoAnswered;
export type TCasePifAutoAnswered = TTypeOfCodec<TCasePifAutoAnsweredCodec>;

export const CasePifTitleMode = union([literal("full"), literal("limited")]);
export type TCasePifTitleModeCodec = typeof CasePifTitleMode;
export type TCasePifTitleMode = TTypeOfCodec<TCasePifTitleModeCodec>;
